import { flatten, isEmpty, uniqBy } from 'ramda'

import type { PickupPoint } from '../../../../../core/typings'
import { PICKUP_DELIVERY_CHANNEL } from '../../../constants'
import { geoCalcPickupSlaToAddress } from './address'

function sortPickupSlasBasedOnAddress(
  pickupSlas: SLA[],
  locationAddress: Address | null
): SLA[] {
  if (isEmpty(pickupSlas)) {
    return []
  }

  const memoizeSlaIds: Record<string, boolean> = {}
  const newPickupSlas: SLA[] = []

  pickupSlas.forEach((sla) => {
    if (!sla || !sla.id || memoizeSlaIds[sla.id]) {
      return
    }

    memoizeSlaIds[sla.id] = true

    newPickupSlas.push({
      ...sla,
      pickupStoreInfo: sla.pickupStoreInfo,
      pickupDistance: geoCalcPickupSlaToAddress(sla, locationAddress),
    })
  })

  newPickupSlas.sort((sla1, sla2) => {
    if (sla1.deliveryChannel !== PICKUP_DELIVERY_CHANNEL) {
      return -1
    }

    if (sla2.deliveryChannel !== PICKUP_DELIVERY_CHANNEL) {
      return 1
    }

    return Number(sla1.pickupDistance) - Number(sla2.pickupDistance)
  })

  return newPickupSlas
}

export function mergeLogisticsInfoWithPickupSlasMap(
  logisticsInfo: LogisticsInfo,
  itemIndexToPickupSlasMap: Record<number, any>, // TODO: types
  locationAddress: Address | null
) {
  if (isEmpty(logisticsInfo) || isEmpty(itemIndexToPickupSlasMap)) {
    return logisticsInfo
  }

  const newLogisticsInfo = logisticsInfo.map((newLogisticsInfoItem) => {
    const { itemIndex } = newLogisticsInfoItem

    return {
      ...newLogisticsInfoItem,
      slas: sortPickupSlasBasedOnAddress(
        [
          ...newLogisticsInfoItem.slas,
          ...(itemIndexToPickupSlasMap[itemIndex] ?? []),
        ],
        locationAddress
      ),
    }
  })

  return newLogisticsInfo
}

export function getUniquePickupSlasFromLogisticsInfo(
  logisticsInfo: LogisticsInfo,
  locationAddress: Address | null
) {
  if (isEmpty(logisticsInfo)) {
    return []
  }

  const logisticsInfoSlas = flatten(logisticsInfo.map((li) => li.slas))

  const logisticsInfoPickupSlas = logisticsInfoSlas.filter(
    (sla) => sla.deliveryChannel === PICKUP_DELIVERY_CHANNEL
  )

  return sortPickupSlasBasedOnAddress(logisticsInfoPickupSlas, locationAddress)
}

export function mergePickupPoints(
  currentPickupPoints: PickupPoint[],
  newPickupPoints: PickupPoint[]
) {
  return uniqBy((pickupPoint) => pickupPoint.id, [
    ...currentPickupPoints,
    ...newPickupPoints,
  ])
}
